/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { EvidencePart, ProgramData, ProgramSkill, SkillsMultiSelectSelectedValue } from "@interfaces/Interfaces";
import { IonItem } from "@ionic/react";
import {
  Button_Checkbox_Active,
  Button_Checkbox_Inactive,
  Button_Collapse,
  Button_Expand,
} from "@assets/images";
import _ from "lodash";
import classNames from "classnames";
import { Text, View } from "@react-pdf/renderer";
import { getDisplayTitleForPDFExport } from "@utils/stringUtils";
import Collapsible from "react-collapsible";
import * as ProgramUtils from "@utils/programUtils";

type Props = {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  definition: EvidencePart;
  onValueChanged?: (value: any) => void;
  data?: any;
  isDisplay?: boolean;
  isPDF?: boolean;
  pdfStyles: any;
  program?: ProgramData;
  initialValue?: SkillsMultiSelectSelectedValue[];
  skills: ProgramSkill[];
  preselectedSkill?: ProgramSkill | null;
};

const getPreselectedSkill = (
  preselectedSkill: ProgramSkill | undefined | null
): SkillsMultiSelectSelectedValue[] | null => {
  if (!preselectedSkill) return null;

  return [
    {
      skillId: preselectedSkill.ID,
      skillName: preselectedSkill.Name,
      topSection: preselectedSkill["Top section"] ?? "",
    },
  ];
};

const SkillsMultiSelect: React.FC<Props> = (props) => {
  const preselectedSkill: SkillsMultiSelectSelectedValue[] | null = useMemo(
    () => getPreselectedSkill(props.preselectedSkill),
    [props.preselectedSkill]
  );

  const [selectedValues, setSelectedValues] = useState<SkillsMultiSelectSelectedValue[]>(
    preselectedSkill ?? props.initialValue ?? []
  );

  const [collapsibleOpen, setCollapsibleOpen] = useState<string[]>([]);

  useEffect(() => {
    if (preselectedSkill) {
      setSelectedValues(preselectedSkill);
      props.onValueChanged?.(preselectedSkill);
    }
  }, [preselectedSkill]);

  const isSPAPRP = ProgramUtils.isSPAPRP(props.program?.ID ?? "");

  const groupedSkills = useMemo(() => _.groupBy(props.skills, (skill) => skill["Top section"]), [props.skills]);
  let mappedSkills: _.Dictionary<ProgramSkill[]>[];

  if (isSPAPRP) {
    mappedSkills = useMemo(
      () =>
        Object.keys(groupedSkills)
          .sort()
          .reverse()
          .map((key) => {
            return _.groupBy(
              groupedSkills[key]!.sort((a, b) => a["Top section"]!.localeCompare(b["Top section"]!)),
              (item) => item["Top section"]!
            );
          }),
      [groupedSkills]
    );
  } else {
    mappedSkills = useMemo(
      () =>
        Object.keys(groupedSkills)
          .sort()
          .map((key) => {
            return _.groupBy(
              groupedSkills[key]!.sort((a, b) => a["Top section"]!.localeCompare(b["Top section"]!)),
              (item) => item["Top section"]!
            );
          }),
      [groupedSkills]
    );
  }

  const toggleValue = (value: ProgramSkill): void => {
    const selected = selectedValues.find((item) => item.skillId === value.ID);

    let newSelectedValues;

    if (selected) {
      newSelectedValues = selectedValues.filter((item) => item.skillId !== value.ID);
    } else {
      newSelectedValues = [
        ...selectedValues,
        { skillId: value.ID, skillName: value.Name, topSection: value["Top section"] ?? "" },
      ];
    }

    setSelectedValues(newSelectedValues);
    props.onValueChanged?.(newSelectedValues);
  };

  const handleCollapsibleToggle = (id: string, close?: boolean): void => {
    const array = _.cloneDeep(collapsibleOpen);
    const index = array.findIndex((item) => item === id);

    if (index > -1) {
      array.splice(index, 1);
    } else {
      !close && array.push(id);
    }

    setCollapsibleOpen(array);
  };

  if (props.isPDF) {
    const groupedData = _.groupBy(props.data ?? [], (skill) => skill.topSection);
    const mappedData = Object.keys(groupedData)
      .sort()
      .map((key) => {
        return _.groupBy(
          groupedData[key]!.sort((a, b) => a.topSection!.localeCompare(b.topSection!)),
          (item) => item.topSection!
        );
      });

    return (
      <View>
        <Text style={props.pdfStyles.reflectionHeader}>{getDisplayTitleForPDFExport(props.definition)}</Text>
        {Array.isArray(props.data) && props.data.length > 0 ? (
          <>
            {mappedData.map((item) => {
              const topSectionKey = Object.keys(item)[0];
              const skillsArray = item[topSectionKey];

              return (
                <View key={topSectionKey}>
                  <Text style={props.pdfStyles.reflectionHeader}>{topSectionKey}</Text>
                  {skillsArray?.map((skill) => (
                    <Text
                      key={skill.skillId}
                      style={props.pdfStyles.evidenceDate}
                    >
                      {skill.skillName}
                    </Text>
                  ))}
                </View>
              );
            })}
          </>
        ) : (
          <Text style={props.pdfStyles.evidenceDate}>{"None"}</Text>
        )}
      </View>
    );
  }

  if (props.isDisplay) {
    const groupedData = _.groupBy(props.data ?? [], (skill) => skill.topSection);
    const mappedData = Object.keys(groupedData)
      .sort()
      .map((key) => {
        return _.groupBy(
          groupedData[key]!.sort((a, b) => a.topSection!.localeCompare(b.topSection!)),
          (item) => item.topSection!
        );
      });

    return (
      <div className="readEvidenceBlock">
        <div className="readEvidenceHeader">{props.definition["Display Title"] || props.definition.Name}</div>
        {Array.isArray(props.data) && props.data.length > 0 ? (
          <>
            {mappedData.map((item) => {
              const topSectionKey = Object.keys(item)[0];
              const skillsArray = item[topSectionKey];

              return (
                <div
                  key={topSectionKey}
                  className="mt-4"
                >
                  <div className="readEvidenceHeader">{topSectionKey}</div>
                  <div className="mt-2">
                    {skillsArray?.map((skill) => (
                      <div
                        key={skill.skillId}
                        className="readEvidenceText"
                      >
                        {skill.skillName}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <div className="readEvidenceText">{"None"}</div>
        )}
        <div className="h-[1px] w-full bg-grey-30 mt-4" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="reflectionContentCard bg-grey-20">
        <div className="reflectionContentCardTitle !mb-0">
          {props.definition["Display Title"] ?? "Tick the skills used"}
          <span className="text-cta-red">
            {(props.definition.options === "Mandatory" || props.definition["option type"] === "Mandatory") &&
              !props.definition["Hide Mandatory tag"] &&
              " (Mandatory)"}
          </span>
        </div>
      </div>
      {mappedSkills.map((item, mainIndex) => {
        const topSectionKey = Object.keys(item)[0];
        const skillsArray = item[topSectionKey];
        const flattenedSectionIDs = skillsArray.flat().map((section) => section.ID);
        const open = collapsibleOpen.includes(topSectionKey);

        const hasSkillsSelected =
          selectedValues.filter((skill) => flattenedSectionIDs.includes(skill.skillId)).length > 0;

        return (
          <div
            key={topSectionKey}
            className={classNames("rounded-md border-[1px] border-solid border-grey-40 bg-white overflow-hidden", {
              "mb-4": mainIndex !== mappedSkills.length - 1,
            })}
          >
            <Collapsible
              trigger={
                <IonItem
                  button
                  detail={false}
                  className="evidencePartsCheckboxContainer"
                  onClick={() => handleCollapsibleToggle(topSectionKey)}
                >
                  <div className="w-full flex flex-row justify-between items-center px-3.5 py-[5px]">
                    <div className="text-17px font-acc-600 leading-default tracking-default text-grey-90">
                      {topSectionKey}
                    </div>
                    <div className="flex flex-row">
                      <img
                        alt=""
                        className="h-icon w-icon img-no-select"
                        src={open ? Button_Collapse : Button_Expand}
                      />
                      <img
                        alt=""
                        className="h-icon w-icon img-no-select"
                        src={hasSkillsSelected ? Button_Checkbox_Active : Button_Checkbox_Inactive}
                      />
                    </div>
                  </div>
                </IonItem>
              }
              transitionTime={250}
              transitionCloseTime={150}
            >
              <div className="px-4 mb-3">
                {skillsArray?.map((skill, index) => {
                  const selected = selectedValues.find((item) => item.skillId === skill.ID);

                  return (
                    <div key={skill.ID}>
                      {index === 0 && (
                        <>
                          <div className="h-[2px] bg-grey-20" />
                        </>
                      )}
                      <IonItem
                        button
                        detail={false}
                        className="evidencePartsCheckboxContainer"
                        onClick={() => toggleValue(skill)}
                      >
                        <div className="w-full flex flex-row justify-between items-center">
                          <div
                            className={classNames("text-acc-15px font-acc-400 text-grey-90", {
                              "font-semibold": selected,
                            })}
                          >
                            {skill.Name}
                          </div>
                          <img
                            alt=""
                            className="h-icon w-icon img-no-select -mr-0.5"
                            src={selected ? Button_Checkbox_Active : Button_Checkbox_Inactive}
                          />
                        </div>
                      </IonItem>
                    </div>
                  );
                })}
              </div>
            </Collapsible>
          </div>
        );
      })}
    </div>
  );
};

export default SkillsMultiSelect;
